import type {
  BookingConfirmationResult,
  BookingService,
  BookingListItem,
  BookingListQuery,
  BookingPaymentView,
  BookingSettings,
  BookingSettingsView,
  BookingSlot,
  CreatedBooking,
  StaffCalendarState,
  GoogleSheetsConnection,
  StaffMember,
  WaitlistEntry,
} from "../booking-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

function bookingQs(q?: BookingListQuery): string {
  const p = new URLSearchParams();
  if (q?.from) p.set("from", q.from);
  if (q?.to) p.set("to", q.to);
  if (q?.status?.length) p.set("status", q.status.join(","));
  if (q?.staffId) p.set("staffId", q.staffId);
  if (q?.serviceId) p.set("serviceId", q.serviceId);
  if (q?.contactId) p.set("contactId", q.contactId);
  if (q?.displayStatus?.length) p.set("displayStatus", q.displayStatus.join(","));
  if (q?.paymentStatus?.length) p.set("paymentStatus", q.paymentStatus.join(","));
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

export type BookingsApiMethods = Pick<
  ApiClient,
  | "listBookingServices"
  | "createBookingService"
  | "updateBookingService"
  | "setBookingServicePattern"
  | "deleteBookingService"
  | "listBookings"
  | "exportBookings"
  | "setBookingStatus"
  | "getBookingPayments"
  | "requestBookingRefund"
  | "resolveBookingRefund"
  | "rescheduleBooking"
  | "getBookingSettings"
  | "createBooking"
  | "sendBookingConfirmation"
  | "listBookingSlots"
  | "updateBookingSettings"
  | "listBookingWaitlist"
  | "listStaff"
  | "createStaff"
  | "updateStaff"
  | "deleteStaff"
  | "setStaffServices"
  | "getStaffCalendar"
  | "connectStaffCalendar"
  | "disconnectStaffCalendar"
  | "getGoogleSheets"
  | "connectGoogleSheets"
  | "disconnectGoogleSheets"
  | "resolveBookingConflict"
>;

export function createBookingsApi({ request, requestCsv, ws }: HttpTransport): BookingsApiMethods {
  return {
    async listBookingServices(id) {
      return (await request<{ services: BookingService[] }>("GET", `${ws(id)}/services`)).services;
    },
    async setBookingServicePattern(id, serviceId, config) {
      await request(
        "PATCH",
        `${ws(id)}/services/${encodeURIComponent(serviceId)}/booking-config`,
        config,
      );
    },
    async createBookingService(id, input) {
      return (await request<{ service: BookingService }>("POST", `${ws(id)}/services`, input))
        .service;
    },
    async updateBookingService(id, serviceId, input) {
      return (
        await request<{ service: BookingService }>(
          "PATCH",
          `${ws(id)}/services/${encodeURIComponent(serviceId)}`,
          input,
        )
      ).service;
    },
    async deleteBookingService(id, serviceId) {
      await request<void>("DELETE", `${ws(id)}/services/${encodeURIComponent(serviceId)}`);
    },
    async listBookings(id, query) {
      return (
        await request<{ bookings: BookingListItem[] }>(
          "GET",
          `${ws(id)}/bookings${bookingQs(query)}`,
        )
      ).bookings;
    },
    exportBookings: (id, query) =>
      requestCsv("GET", `${ws(id)}/bookings/export.csv${bookingQs(query)}`),
    getBookingPayments: (id, bookingId) =>
      request<BookingPaymentView>(
        "GET",
        `${ws(id)}/bookings/${encodeURIComponent(bookingId)}/payments`,
      ),
    async requestBookingRefund(id, bookingId, input) {
      await request(
        "POST",
        `${ws(id)}/bookings/${encodeURIComponent(bookingId)}/refund-requests`,
        input,
      );
    },
    async resolveBookingRefund(id, bookingId, eventId, decision) {
      await request(
        "POST",
        `${ws(id)}/bookings/${encodeURIComponent(bookingId)}/refund-requests/${encodeURIComponent(eventId)}/${decision}`,
        {},
      );
    },
    async setBookingStatus(id, bookingId, status) {
      await request("PATCH", `${ws(id)}/bookings/${encodeURIComponent(bookingId)}/status`, {
        status,
      });
    },
    async rescheduleBooking(id, bookingId, start, partySize) {
      await request("POST", `${ws(id)}/bookings/${encodeURIComponent(bookingId)}/reschedule`, {
        start,
        ...(partySize !== undefined ? { partySize } : {}),
      });
    },
    getBookingSettings: (id) => request<BookingSettingsView>("GET", `${ws(id)}/booking-settings`),
    async createBooking(id, input) {
      return (await request<{ booking: CreatedBooking }>("POST", `${ws(id)}/bookings`, input))
        .booking;
    },
    sendBookingConfirmation: (id, bookingId, conversationId) =>
      request<BookingConfirmationResult>(
        "POST",
        `${ws(id)}/bookings/${encodeURIComponent(bookingId)}/send-confirmation`,
        { conversationId },
      ),
    async listBookingSlots(id, query) {
      const p = new URLSearchParams({ serviceId: query.serviceId, date: query.date });
      if (query.staffId) p.set("staffId", query.staffId);
      if (query.partySize !== undefined) p.set("partySize", String(query.partySize));
      return (
        await request<{ slots: BookingSlot[] }>("GET", `${ws(id)}/bookings/slots?${p.toString()}`)
      ).slots;
    },
    async updateBookingSettings(id, patch) {
      return (
        await request<{ settings: BookingSettings }>("PUT", `${ws(id)}/booking-settings`, patch)
      ).settings;
    },
    async listBookingWaitlist(id, filter) {
      const q = filter?.status ? `?status=${filter.status}` : "";
      return (await request<{ entries: WaitlistEntry[] }>("GET", `${ws(id)}/bookings/waitlist${q}`))
        .entries;
    },
    async listStaff(id) {
      return (await request<{ staff: StaffMember[] }>("GET", `${ws(id)}/staff`)).staff;
    },
    async createStaff(id, input) {
      return (await request<{ staff: StaffMember }>("POST", `${ws(id)}/staff`, input)).staff;
    },
    async updateStaff(id, staffId, input) {
      return (
        await request<{ staff: StaffMember }>(
          "PATCH",
          `${ws(id)}/staff/${encodeURIComponent(staffId)}`,
          input,
        )
      ).staff;
    },
    async deleteStaff(id, staffId) {
      await request<void>("DELETE", `${ws(id)}/staff/${encodeURIComponent(staffId)}`);
    },
    async setStaffServices(id, staffId, serviceIds) {
      return (
        await request<{ staff: StaffMember }>(
          "PUT",
          `${ws(id)}/staff/${encodeURIComponent(staffId)}/services`,
          { serviceIds },
        )
      ).staff;
    },
    async getStaffCalendar(id, staffId) {
      return request<StaffCalendarState>(
        "GET",
        `${ws(id)}/staff/${encodeURIComponent(staffId)}/calendar`,
      );
    },
    async connectStaffCalendar(id, staffId) {
      return request<{ url: string }>(
        "POST",
        `${ws(id)}/staff/${encodeURIComponent(staffId)}/calendar/connect`,
        {},
      );
    },
    async disconnectStaffCalendar(id, staffId) {
      await request(
        "POST",
        `${ws(id)}/staff/${encodeURIComponent(staffId)}/calendar/disconnect`,
        {},
      );
    },
    getGoogleSheets: (id) =>
      request<GoogleSheetsConnection>("GET", `${ws(id)}/integrations/google-sheets`),
    connectGoogleSheets: (id) =>
      request<{ url: string }>("POST", `${ws(id)}/integrations/google-sheets/connect`, {}),
    async disconnectGoogleSheets(id, force = false) {
      await request("POST", `${ws(id)}/integrations/google-sheets/disconnect`, { force });
    },
    async resolveBookingConflict(id, bookingId) {
      await request(
        "POST",
        `${ws(id)}/bookings/${encodeURIComponent(bookingId)}/resolve-conflict`,
        {},
      );
    },
  };
}
